import { useState, useEffect } from 'react'
import contactDetailsData from '@/data/contact-details.json'

interface ContactDetail {
  id: string
  type: string
  label: string
  value: string
  show_in_contact_page: boolean
  show_in_footer: boolean
  is_active: boolean
  display_order: number
  icon_name?: string | null
  custom_icon_url?: string | null
}

export const useContactDetails = (location?: 'contact' | 'footer') => {
  const [contactDetails, setContactDetails] = useState<ContactDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchContactDetails = () => {
      try {
        let filtered = (contactDetailsData as ContactDetail[])
          .filter(detail => detail.is_active)

        // Filter based on location
        if (location === 'contact') {
          filtered = filtered.filter(detail => detail.show_in_contact_page)
        } else if (location === 'footer') {
          filtered = filtered.filter(detail => detail.show_in_footer)
        }

        // Sort by display_order
        filtered.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))

        setContactDetails(filtered)
      } catch (err) {
        console.error('Error fetching contact details:', err)
        setError('Failed to fetch contact details')
      } finally {
        setLoading(false)
      }
    }

    fetchContactDetails()
  }, [location])

  // Helper functions to get specific types of contact details
  const getContactDetailsByType = (type: string) => {
    return contactDetails.filter(detail => detail.type === type)
  }

  const getEmails = () => getContactDetailsByType('email')
  const getPhones = () => getContactDetailsByType('phone')
  const getAddresses = () => getContactDetailsByType('address')
  const getSocialLinks = () => getContactDetailsByType('social')
  const getNotes = () => getContactDetailsByType('note')

  return {
    contactDetails,
    loading,
    error,
    getContactDetailsByType,
    getEmails,
    getPhones,
    getAddresses,
    getSocialLinks,
    getNotes
  }
}
